import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, CheckCircle2, Rocket } from "lucide-react";

export default function SharedResume() {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/share/resume/${shareId}`).then(({ data }) => setData(data)).catch((e) => setError(e?.response?.data?.detail || "Not found"));
  }, [shareId]);

  const TEMPLATE_ACCENTS = {
    modern: "#4338ca", tech: "#0ea5e9", finance: "#0f766e", creative: "#ec4899",
    minimalist: "#0A0A0A", academic: "#7c3aed", executive: "#111827",
    student: "#eab308", healthcare: "#0891b2", trades: "#d97706",
  };

  if (error) return (
    <div className="min-h-[60vh] flex items-center justify-center px-5">
      <div className="nb-card p-10 max-w-md text-center">
        <h1 className="text-2xl font-black">Resume not found</h1>
        <p className="text-gray-600 mt-2">{error}</p>
        <Link to="/" className="mt-6 inline-block nb-btn bg-[#4338ca] text-white px-5 py-2.5">Go to Career AI Hub</Link>
      </div>
    </div>
  );
  if (!data) return <div className="p-20 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto" /></div>;

  const accent = TEMPLATE_ACCENTS[(data.template || "modern").toLowerCase()] || "#4338ca";

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-8 py-10" data-testid="shared-resume">
      <div className="mb-6 flex items-center justify-between text-xs text-gray-500">
        <span>Shared via Career AI Hub · {new Date(data.created_at).toLocaleDateString()}</span>
        <Link to="/" className="hover:underline inline-flex items-center gap-1 font-bold"><Rocket className="w-3.5 h-3.5" /> Career AI Hub</Link>
      </div>
      <div className="nb-card p-8 md:p-10 bg-white">
        <div style={{ borderBottom: `2px solid ${accent}`, paddingBottom: 12 }}>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter" style={{ color: accent }}>
            {data.candidate_name || "Candidate"}
          </h1>
          {data.contact_line && <div className="mt-1 text-sm text-gray-600">{data.contact_line}</div>}
          {(data.job_title || data.company) && (
            <div className="mt-1 text-sm italic text-gray-600">
              For: {data.job_title || ""}{data.company ? ` @ ${data.company}` : ""}
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <span className="nb-pill" style={{ backgroundColor: accent, color: "#fff", borderColor: "#0A0A0A" }}>
              {(data.template || "modern").toUpperCase()}
            </span>
            {data.match_score != null && (
              <span className="nb-pill bg-[#22c55e] text-white">{data.match_score}% fit</span>
            )}
          </div>
        </div>

        {data.summary && (
          <div className="mt-6">
            <div className="label-eyebrow" style={{ color: accent }}>Summary</div>
            <p className="mt-1 text-gray-800 leading-relaxed">{data.summary}</p>
          </div>
        )}
        {data.bullets?.length > 0 && (
          <div className="mt-6">
            <div className="label-eyebrow" style={{ color: accent }}>Experience highlights</div>
            <ul className="mt-2 space-y-2">
              {data.bullets.map((b, i) => (
                <li key={i} className="flex gap-2 text-sm text-gray-800">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color: accent }} strokeWidth={2.5} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {data.matched_keywords?.length > 0 && (
          <div className="mt-6">
            <div className="label-eyebrow" style={{ color: accent }}>Key skills</div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {data.matched_keywords.slice(0, 20).map((k) => (
                <span key={k} className="nb-pill" style={{ backgroundColor: accent, color: "#fff" }}>{k}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 text-center text-xs text-gray-500">
        Want your own tailored resume? <Link to="/" className="font-bold underline">Try Career AI Hub free</Link>
      </div>
    </div>
  );
}
